import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { LIMITES } from '../limites';
import { Zap, Heart, Star, Repeat, Clock, Trophy, CheckCircle, XCircle, Mic, Loader, Volume2 } from 'lucide-react';
import './TimeTraveler.css';

const INITIAL_LIVES = 3;
const ALL_CATEGORIES = {
    "present_simple": { name: "Present Simple", icon: "☀️" },
    "past_simple": { name: "Past Simple", icon: "🕰️" },
    "future_simple": { name: "Future Simple", icon: "🔮" },
    "present_continuous": { name: "Present Continuous", icon: "🏃" },
    "past_continuous": { name: "Past Continuous", icon: "🌊" },
    "present_perfect": { name: "Present Perfect", icon: "✅" },
    "past_perfect": { name: "Past Perfect", icon: "💎" },
    "future_perfect": { name: "Future Perfect", icon: "🎯" },
    "future_continuous": { name: "Future Continuous", icon: "⏩" },
    "future_perfect_continuous": { name: "Future Perfect Continuous", icon: "🚀" },
    "present_perfect_continuous": { name: "Present Perfect Continuous", icon: "⏳" },
    "past_perfect_continuous": { name: "Past Perfect Continuous", icon: "😁" },
};
const STORAGE_KEY = 'timeTravelerSeenSentences';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = SpeechRecognition ? new SpeechRecognition() : null;

function TimeTraveler() {
    const [gameState, setGameState] = useState('menu');
    const [mode, setMode] = useState(null);
    const [difficulty, setDifficulty] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(INITIAL_LIVES);
    const [streak, setStreak] = useState(0);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('timeTravelerHighScore') || '0', 10));
    const [seenSentences, setSeenSentences] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [userInput, setUserInput] = useState('');
    const [showCorrectionCard, setShowCorrectionCard] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [showHint, setShowHint] = useState(false);
    const hintTimerRef = useRef(null);
    const inputRef = useRef(null);

    // ⭐ shuffledOptions estable con useState + useEffect (Fragmento 4)
    const [shuffledOptions, setShuffledOptions] = useState([]);

    const currentQuestion = useMemo(() => questions[currentQuestionIndex], [questions, currentQuestionIndex]);
    const baseTense = useMemo(() => currentQuestion?.base_tense, [currentQuestion]);
    const changeTense = useMemo(() => currentQuestion?.change, [currentQuestion]);
    const correctAnswer = useMemo(() => currentQuestion?.correct_answer, [currentQuestion]);

    useEffect(() => {
        if (!currentQuestion || mode !== 'multiple') {
            setShuffledOptions([]);
            return;
        }
        const opts = [
            currentQuestion.correct_answer,
            ...Object.values(currentQuestion.transformations)
        ];
        // Fisher-Yates
        for (let i = opts.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [opts[i], opts[j]] = [opts[j], opts[i]];
        }
        setShuffledOptions(opts.slice(0, 4));
    }, [currentQuestion, mode]);

    useEffect(() => {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        setSeenSentences(new Set(stored));
    }, []);

    const handleShowHint = useCallback(() => {
        if (showHint) return;
        setShowHint(true);
        hintTimerRef.current = setTimeout(() => setShowHint(false), 10000);
    }, [showHint]);

    useEffect(() => {
        return () => clearTimeout(hintTimerRef.current);
    }, []);

    // ⭐ Speech recognition con limpieza correcta (Fragmento 6)
    useEffect(() => {
        if (!recognition) return;

        const onStart = () => setIsListening(true);
        const onEnd = () => setIsListening(false);
        const onResult = (event) => {
            const transcript = event.results[0][0].transcript;
            setUserInput(transcript);
        };
        const onError = (event) => {
            console.error("[TT] Speech recognition error:", event.error);
            setIsListening(false);
        };

        recognition.addEventListener('start', onStart);
        recognition.addEventListener('end', onEnd);
        recognition.addEventListener('result', onResult);
        recognition.addEventListener('error', onError);

        return () => {
            recognition.removeEventListener('start', onStart);
            recognition.removeEventListener('end', onEnd);
            recognition.removeEventListener('result', onResult);
            recognition.removeEventListener('error', onError);
            if (isListening) recognition.abort();
        };
    }, []);

    useEffect(() => {
        if (!isLoading) return;
        const messages = [
            "Conectando con la Inteligencia Artificial...",
            "Construyendo oraciones en inglés...",
            "Afinando los tiempos verbales...",
            "Diseñando el viaje temporal...",
            "Casi listos para viajar..."
        ];
        let i = 0;
        setLoadingMessage(messages[0]);
        const interval = setInterval(() => {
            i = (i + 1) % messages.length;
            setLoadingMessage(messages[i]);
        }, 2500);
        return () => clearInterval(interval);
    }, [isLoading]);

    const startGame = async () => {
        setIsLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/api/minigames/get-time-traveler-round', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    difficulty: difficulty,
                    categories: selectedCategories,
                    seen_sentences: Array.from(seenSentences),
                }),
            });
            if (!response.ok) throw new Error('No sentences found for this configuration.');
            const data = await response.json();
            if (data.length < 1) {
                setError("Not enough unique sentences available. Please try again later.");
                setGameState('menu');
                return;
            }
            setQuestions(data);
            setCurrentQuestionIndex(0);
            setScore(0);
            setLives(INITIAL_LIVES);
            setStreak(0);
            setCorrectAnswers(0);
            setFeedback(null);
            setShowHint(false);
            setGameState('playing');
        } catch (err) {
            setError(err.message);
            setGameState('menu');
        } finally {
            setIsLoading(false);
        }
    };

    const endGame = useCallback((finalScore = score, finalCorrect = correctAnswers) => {
        if (finalScore > highScore) {
            setHighScore(finalScore);
            localStorage.setItem('timeTravelerHighScore', finalScore.toString());
        }
        const saveScore = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                if (finalCorrect > 0) {
                    await fetch('/api/minigames/save-score', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            game_name: 'time_traveler',
                            score: finalScore,
                            correct_answers: finalCorrect,
                            total_questions: questions.length
                        })
                    });
                }
            } catch (error) {
                console.error("Error guardando XP:", error);
            }
        };
        saveScore();

        const newSentencesPlayed = questions.map(q => q.base_sentence);
        const updatedSentences = new Set([...seenSentences, ...newSentencesPlayed]);
        setSeenSentences(updatedSentences);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(updatedSentences)));
        setGameState('gameOver');
    }, [highScore, questions, seenSentences, score, correctAnswers]);

    const handleAnswer = useCallback((answer = userInput) => {
        if (feedback || showCorrectionCard) return;

        const normalize = (str) => str.toLowerCase().trim().replace(/[.,!?;]/g, '');
        const userNorm = normalize(answer);
        const correctNorm = normalize(correctAnswer);
        const isCorrect = [correctNorm, ...(currentQuestion.variants || []).map(normalize)].includes(userNorm);

        if (isCorrect) {
            const points = 10 + streak * 2;
            const newScore = score + points;
            const newCorrect = correctAnswers + 1;
            setScore(newScore);
            setCorrectAnswers(newCorrect);
            setStreak(s => s + 1);
            setFeedback({ type: 'correct', message: currentQuestion.feedback.correct });
            setTimeout(() => {
                if (currentQuestionIndex < questions.length - 1) {
                    setCurrentQuestionIndex(i => i + 1);
                    setFeedback(null);
                    setUserInput('');
                    setShowHint(false);
                } else {
                    endGame(newScore, newCorrect);
                }
            }, 1500);
        } else {
            setStreak(0);
            setLives(l => l - 1);
            setFeedback({
                type: 'incorrect',
                message: `Incorrecto: ${correctAnswer}`,
            });
            setShowCorrectionCard(true);
            if (lives <= 1) setTimeout(() => endGame(score, correctAnswers), 2000);
        }
    }, [userInput, currentQuestion, feedback, showCorrectionCard, currentQuestionIndex, lives, streak, endGame, correctAnswer, score, correctAnswers]);

    const moveToNext = () => {
        setShowCorrectionCard(false);
        setFeedback(null);
        setUserInput('');
        setShowHint(false);
        if (currentQuestionIndex < questions.length - 1) setCurrentQuestionIndex(i => i + 1);
        else endGame(score, correctAnswers);
    };

    const handleToggleListening = () => {
        if (!recognition) {
            alert("Lo siento, tu navegador no soporta el reconocimiento de voz. Usa Chrome o Edge.");
            return;
        }
        if (isListening) {
            recognition.stop();
        } else {
            setUserInput('');
            recognition.lang = 'en-US';
            recognition.start();
        }
    };

    const renderCorrectionCard = () => (
        <div className="tt-feedback-overlay">
            <div className="tt-feedback-card">
                <XCircle size={48} className="error-icon" />
                <h2>¡Respuesta Incorrecta!</h2>
                <p>La respuesta correcta es:</p>
                <strong>{correctAnswer}</strong>
                <button className="tt-play-btn" onClick={moveToNext} aria-label="Next Question">Next</button>
            </div>
        </div>
    );

    const renderMenu = () => {
        if (isLoading) {
            return (
                <div className="tt-loading-container">
                    <Loader size={64} className="animate-spin" />
                    <h3 className="tt-loading-text">{loadingMessage}</h3>
                    <p style={{marginTop: '1rem', color: 'var(--text-secondary)'}}>Esto puede tomar unos segundos...</p>
                </div>
            );
        }

        return (
        <div className="tt-menu">
            <div className="tt-header">
                <Clock size={48} />
                <h1>Time Traveler</h1>
                <p>Rewrite sentences through time and master verb tenses.</p>
            </div>
            <div className="tt-dashboard">
                <div><Star size={16} /><span>High Score</span><strong>{highScore}</strong></div>
                <div><Zap size={16} /><span>Streak</span><strong>0</strong></div>
                <div><Heart size={16} /><span>Lives</span><strong>{INITIAL_LIVES}</strong></div>
            </div>
            <div className="tt-config-section">
                <h3>1. Choose Difficulty</h3>
                <div className="tt-difficulty-options">
                    <button onClick={() => setDifficulty(1)} className={`tt-btn ${difficulty === 1 ? 'active' : ''}`}>Easy</button>
                    <button onClick={() => setDifficulty(2)} className={`tt-btn ${difficulty === 2 ? 'active' : ''}`}>Medium</button>
                    <button onClick={() => setDifficulty(3)} className={`tt-btn ${difficulty === 3 ? 'active' : ''}`}>Hard</button>
                </div>
            </div>
            <div className="tt-config-section">
                <h3>2. Choose Mode</h3>
                <div className="tt-difficulty-options">
                    <button onClick={() => setMode('multiple')} className={`tt-btn ${mode === 'multiple' ? 'active' : ''}`}>Multiple Choice</button>
                    <button onClick={() => setMode('writing')} className={`tt-btn ${mode === 'writing' ? 'active' : ''}`}>Writing or Speaking</button>
                </div>
            </div>

            {/* ⭐ Selector de categorías (Fragmento 5) */}
            <div className="tt-config-section">
                <h3>3. Choose Tenses</h3>
                <div className="tt-category-grid">
                    <button
                        className={`tt-btn tt-btn-sm ${selectedCategories.length === Object.keys(ALL_CATEGORIES).length ? 'active' : ''
                            }`}
                        onClick={() => setSelectedCategories(Object.keys(ALL_CATEGORIES))}
                    >
                        All tenses
                    </button>
                    {Object.entries(ALL_CATEGORIES).map(([key, { name, icon }]) => (
                        <button
                            key={key}
                            className={`tt-btn tt-btn-sm ${selectedCategories.includes(key) ? 'active' : ''}`}
                            onClick={() =>
                                setSelectedCategories(prev =>
                                    prev.includes(key)
                                        ? prev.length > 1 ? prev.filter(k => k !== key) : prev
                                        : [...prev, key]
                                )
                            }
                            aria-label={`Toggle ${name}`}
                            aria-pressed={selectedCategories.includes(key)}
                        >
                            {icon} {name}
                        </button>
                    ))}
                </div>
            </div>

            {error && <p className="tt-error-message">{error}</p>}
            <button className="tt-play-btn" onClick={startGame} disabled={!difficulty || !mode || selectedCategories.length === 0}>
                Start Journey!
            </button>
        </div>
        );
    };

    const renderMultipleChoice = () => (
        <div className="tt-playing-view">
            {showCorrectionCard && renderCorrectionCard()}
            <div className="tt-game-hud">
                <div className="tt-hud-item score"><Star size={16} /> {score}</div>
                <div className="tt-hud-item lives">
                    {Array.from({ length: INITIAL_LIVES }).map((_, i) => (
                        <Heart key={i} size={20} className={i < lives ? 'filled' : 'empty'} />
                    ))}
                </div>
                <div className="tt-hud-item streak"><Zap size={16} /> x{streak}</div>

                {/* Barra de progreso */}
                <div className="tt-progress-bar-wrapper" aria-label={`Question ${currentQuestionIndex + 1} of ${questions.length}`}>
                    <div
                        className="tt-progress-bar-fill"
                        style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                    />
                    <span className="tt-progress-label">
                        {currentQuestionIndex + 1} / {questions.length}
                    </span>
                </div>
            </div>
            <div className="tt-game-area">
                <div className="tt-question-card">
                    <blockquote className="tt-base-sentence">"{currentQuestion?.base_sentence}"</blockquote>
                    <p className="tt-prompt-label">Transform to:</p>
                    <p className="tt-target-tense">{ALL_CATEGORIES[changeTense]?.name || changeTense}</p>
                    <div className="tt-answer-grid">
                        {shuffledOptions.map((option, index) => (
                            <button
                                key={index}
                                className={`tt-option-btn ${feedback && option === correctAnswer ? 'correct' : feedback && option === userInput ? 'incorrect' : ''}`}
                                onClick={() => handleAnswer(option)}
                                disabled={!!feedback || showCorrectionCard}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                    <button className="tt-hint-btn" onClick={handleShowHint} disabled={showHint || !!feedback || showCorrectionCard} />
                    {showHint && (
                        <div className="tt-hint-box">
                            <h4>Pista</h4>
                            <p>{currentQuestion.context || 'No context available.'}</p>
                            <h4>Learning Objective</h4>
                            <p>{currentQuestion.learning_objective || 'No learning objective available.'}</p>
                        </div>
                    )}
                    {feedback && (
                        <div className={`tt-feedback-box ${feedback.type}`} aria-live="polite">
                            {feedback.type === 'correct' ? <CheckCircle /> : <XCircle />}
                            {feedback.message}
                            {feedback.type === 'incorrect' && <strong>Incorrecto: {correctAnswer}</strong>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderWriting = () => (
        <div className="tt-playing-view">
            {showCorrectionCard && renderCorrectionCard()}
            <div className="tt-game-hud">
                <div className="tt-hud-item score"><Star size={16} /> {score}</div>
                <div className="tt-hud-item lives">
                    {Array.from({ length: INITIAL_LIVES }).map((_, i) => (
                        <Heart key={i} size={20} className={i < lives ? 'filled' : 'empty'} />
                    ))}
                </div>
                <div className="tt-hud-item streak"><Zap size={16} /> x{streak}</div>

                {/* Barra de progreso */}
                <div className="tt-progress-bar-wrapper" aria-label={`Question ${currentQuestionIndex + 1} of ${questions.length}`}>
                    <div
                        className="tt-progress-bar-fill"
                        style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                    />
                    <span className="tt-progress-label">
                        {currentQuestionIndex + 1} / {questions.length}
                    </span>
                </div>
            </div>
            <div className="tt-game-area">
                <div className="tt-question-card">
                    <blockquote className="tt-base-sentence">"{currentQuestion?.base_sentence}"</blockquote>
                    <p className="tt-prompt-label">Transform to:</p>
                    <p className="tt-target-tense">{ALL_CATEGORIES[changeTense]?.name || changeTense}</p>
                    <div className="tt-writing-input">
                        <textarea
                            maxLength={LIMITES.respuestaEjercicio}
                            ref={inputRef}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAnswer()}
                            placeholder="Write or speak your answer..."
                            rows="3"
                            className="tt-textarea"
                        />
                        <div className="tt-action-buttons">
                            <button onClick={() => handleAnswer()} disabled={!userInput.trim() || !!feedback || showCorrectionCard} className="tt-confirm-btn">Confirmar</button>
                            <button onClick={() => {
                                if ('speechSynthesis' in window) {
                                    const utterance = new SpeechSynthesisUtterance(currentQuestion.base_sentence);
                                    utterance.lang = 'en-US';
                                    window.speechSynthesis.speak(utterance);
                                } else alert("Tu navegador no soporta síntesis de voz.");
                            }} disabled={!!feedback || showCorrectionCard} className="tt-speak-btn"><Volume2 size={24} /></button>
                            <button onClick={handleToggleListening} disabled={!!feedback || showCorrectionCard || isListening} className={`tt-mic-btn ${isListening ? 'listening' : ''}`}><Mic size={24} /></button>
                            <button className="tt-hint-btn" onClick={handleShowHint} disabled={showHint || !!feedback || showCorrectionCard} />
                        </div>
                    </div>
                    {showHint && (
                        <div className="tt-hint-box">
                            <h4>Pista</h4>
                            <p>{currentQuestion.context || 'No context available.'}</p>
                            <h4>Learning Objective</h4>
                            <p>{currentQuestion.learning_objective || 'No learning objective available.'}</p>
                        </div>
                    )}
                    {feedback && (
                        <div className={`tt-feedback-box ${feedback.type}`} aria-live="polite">
                            {feedback.type === 'correct' ? <CheckCircle /> : <XCircle />}
                            {feedback.message}
                            {feedback.type === 'incorrect' && <strong>Incorrecto: {correctAnswer}</strong>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderGame = () => {
        if (mode === 'multiple') return renderMultipleChoice();
        if (mode === 'writing') return renderWriting();
        return null;
    };

    const renderGameOver = () => (
        <div className="tt-game-over-screen">
            <Trophy size={48} />
            <h2>¡Viaje Completado!</h2>
            <p>Tu puntaje final fue:</p>
            <strong className="tt-final-score">{score}</strong>
            <button className="tt-play-btn" onClick={() => setGameState('menu')}>
                <Repeat size={16} /> Play Again
            </button>
        </div>
    );

    return (
        <div className="tt-container">
            <div className={`tt-inner-container ${gameState}`}>
                {gameState === 'menu' && renderMenu()}
                {gameState === 'playing' && currentQuestion && renderGame()}
                {gameState === 'gameOver' && renderGameOver()}
            </div>
        </div>
    );
}

export default TimeTraveler;